import { createTheme } from '@mui/material/styles';

// ─────────────────────────────────────────────────────────────────────────────
// TOKENS DE COLOR — solo para uso interno del theme MUI global (palette).
// ⚠️  NO importar estos tokens en componentes que reciben contenido del CMS.
//     Los colores de componentes CMS vienen de component.theme.themeStyles (BD).
//     Ver regla en AVANTTHYA_CONTEXT_FINAL.md § 10-A.
// ─────────────────────────────────────────────────────────────────────────────
const NAVY     = '#001933';
const BLUE_MID = '#23398F';
const RED_MAIN = '#db1d1f';

export const MAIN_COLOR      = RED_MAIN;   // alias legacy
export const SECONDARY_COLOR = '#001230';  // alias legacy

// ─────────────────────────────────────────────────────────────────────────────
// BREAKPOINTS OFICIALES
//   xs  → <430px      (mobile pequeño)
//   sm  → 430–767px   (Mobile  oficial: 430px)
//   md  → 768–1365px  (Tablet  oficial: 768px)
//   lg  → 1366px+     (Desktop oficial: 1366px)
//   xl  → 1920px+
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// ESCALA TIPOGRÁFICA — Avantthya Design System v2
//
// clamp(min, fluid, max) escala suavemente entre Mobile (430px) y Desktop (1366px).
// Base: 16px = 1rem. Breakpoints: sm=430px | md=768px | lg=1366px
//
// fluid_vw = (desktop_px - mobile_px) / (1366 - 430) * 100
// clamp offset = mobile_px - fluid_vw * 430/100
//
// Estilo        Desktop    Tablet     Mobile    L-H   Peso  Uso principal
// ─────────────────────────────────────────────────────────────────────────
// Display/Hero  64px       48px       36px      1.1   700   Título único principal
// Heading L     40px       32px       28px      1.2   700   Títulos de secciones
// Heading M     28px       24px       22px      1.3   600   Títulos de bloques/cards
// Heading S     24px       22px       20px      1.4   600   Subsecciones internas
// Heading XS    20px       18px       18px      1.4   600   Títulos widgets/sidebar
// Heading XXS   18px       16px       16px      1.4   700   Títulos muy pequeños (Caps)
// Body Regular  16px       16px       15px      1.6   400   Texto de lectura general
// Body Small    12px       12px       11px      1.5   400   Notas y aclaraciones
// Subtitle 1    18px       16px       16px      1.5   400   Subtítulo hero, intro
// Subtitle 2    14px       14px       13px      1.5   500   Subtítulos secundarios
// Button        16px       15px       14px      —     700   CTAs
// Caption       12px       12px       11px      1.4   400   Disclaimers extra pequeños
// ─────────────────────────────────────────────────────────────────────────────

const theme = createTheme({

  palette: {
    primary:   { main: NAVY },
    secondary: { main: RED_MAIN },
    info:      { main: BLUE_MID },
    error:     { main: '#FA9999' },
    success:   { main: '#2F9324' },
    warning:   { main: '#FFD88B' },
  },

  typography: {
    fontFamily: '"Segoe UI", Arial, sans-serif',

    // Display Hero <h1> — Mobile:36px Tablet:48px Desktop:64px  LH:1.1  W:700
    // fluid: (64-36)/(1366-430)*100 = 2.991vw | offset: 36 - 2.991*4.30 = 23.14px = 1.446rem
    h1: {
      fontSize: 'clamp(2.25rem, 1.446rem + 2.991vw, 4rem)', // 36px → 64px
      fontWeight: 700,
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },

    // Heading L <h2> — Mobile:28px Tablet:32px Desktop:40px  LH:1.2  W:700
    // fluid: (40-28)/(1366-430)*100 = 1.282vw | offset: 28 - 1.282*4.30 = 22.49px = 1.406rem
    h2: {
      fontSize: 'clamp(1.75rem, 1.406rem + 1.282vw, 2.5rem)', // 28px → 40px
      fontWeight: 700,
      lineHeight: 1.2,
    },

    // Heading M <h3> — Mobile:22px Tablet:24px Desktop:28px  LH:1.3  W:600
    // fluid: (28-22)/(1366-430)*100 = 0.641vw | offset: 22 - 0.641*4.30 = 19.24px = 1.202rem
    h3: {
      fontSize: 'clamp(1.375rem, 1.202rem + 0.641vw, 1.75rem)', // 22px → 28px
      fontWeight: 600,
      lineHeight: 1.3,
    },

    // Heading S <h4> — Mobile:20px Tablet:22px Desktop:24px  LH:1.4  W:600
    // fluid: (24-20)/(1366-430)*100 = 0.427vw | offset: 20 - 0.427*4.30 = 18.16px = 1.135rem
    h4: {
      fontSize: 'clamp(1.25rem, 1.135rem + 0.427vw, 1.5rem)', // 20px → 24px
      fontWeight: 600,
      lineHeight: 1.4,
    },

    // Heading XS <h5> — Mobile:18px Tablet:18px Desktop:20px  LH:1.4  W:600
    // fluid: (20-18)/(1366-430)*100 = 0.214vw | offset: 18 - 0.214*4.30 = 17.08px = 1.067rem
    h5: {
      fontSize: 'clamp(1.125rem, 1.067rem + 0.214vw, 1.25rem)', // 18px → 20px
      fontWeight: 600,
      lineHeight: 1.4,
    },

    // Heading XXS <h6> — Mobile:16px Tablet:16px Desktop:18px  LH:1.4  W:700
    // fluid: (18-16)/(1366-430)*100 = 0.214vw | offset: 16 - 0.214*4.30 = 15.08px = 0.942rem
    h6: {
      fontSize: 'clamp(1rem, 0.942rem + 0.214vw, 1.125rem)', // 16px → 18px
      fontWeight: 700,
      lineHeight: 1.4,
      letterSpacing: '0.01em',
    },

    // Body Regular <p> — Mobile:15px Tablet:16px Desktop:16px  LH:1.6  W:400
    body1: {
      fontSize: 'clamp(0.9375rem, 0.91rem + 0.107vw, 1rem)', // 15px → 16px
      fontWeight: 400,
      lineHeight: 1.6,
    },

    // Body Small <small> — Mobile:11px Tablet:12px Desktop:12px  LH:1.5  W:400
    body2: {
      fontSize: 'clamp(0.6875rem, 0.661rem + 0.107vw, 0.75rem)', // 11px → 12px
      fontWeight: 400,
      lineHeight: 1.5,
    },

    // Caption — 11px→12px (alias de body2, para notas y disclaimers)
    caption: {
      fontSize: 'clamp(0.6875rem, 0.661rem + 0.107vw, 0.75rem)', // 11px → 12px
      fontWeight: 400,
      lineHeight: 1.4,
      display: 'block',
    },

    // Subtitle 1 — Mobile:16px Desktop:18px  LH:1.5  W:400  (intro párrafos, hero sub)
    subtitle1: {
      fontSize: 'clamp(1rem, 0.942rem + 0.214vw, 1.125rem)', // 16px → 18px
      fontWeight: 400,
      lineHeight: 1.5,
    },

    // Subtitle 2 — Mobile:13px Desktop:14px  LH:1.5  W:500  (subtítulos secundarios)
    subtitle2: {
      fontSize: 'clamp(0.8125rem, 0.785rem + 0.107vw, 0.875rem)', // 13px → 14px
      fontWeight: 500,
      lineHeight: 1.5,
    },

    // Button — Mobile:14px Desktop:16px  W:700
    button: {
      fontSize: 'clamp(0.875rem, 0.821rem + 0.214vw, 1rem)', // 14px → 16px
      fontWeight: 700,
      letterSpacing: '0.02em',
      textTransform: 'none',
    },

    // Overline — 11px fijo (etiquetas ALL-CAPS: "SOFIPO", reguladores)
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
  },

  breakpoints: {
    values: {
      xs: 0,     // <430px
      sm: 430,   // Mobile  oficial 430px
      md: 768,   // Tablet  oficial 768px
      lg: 1366,  // Desktop oficial 1366px
      xl: 1920,
    },
  },

  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          fontWeight: 700,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
        body: {
          fontFamily: '"Segoe UI", Arial, sans-serif',
          fontSize: '1rem',
          lineHeight: 1.6,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
  },

  shape: {
    borderRadius: 8,
  },
});

export default theme;
